import { Injectable } from '@angular/core';
import { DateRange, DynamicRange } from '@shared/utils/date-range';
import { RANGE_PRESETS, RangePreset } from '@shared/services/calendar-data.service';
import { BaseLogRequest, ContextEnum } from '@shared/models/ContextSiteModel';
import moment from 'moment';
import { ParamMap } from '@angular/router';
import { SitesService } from '@shared/services/sites/sites.service';
import { Filter, FiltersEnum } from '../../../theme/my-logs/filters/filters';
import { FilterBridgeService } from '@shared/services/logs/filter-bridge.service';
import { FiltersComponent } from '../../../theme/my-logs/filters/filters.component';

@Injectable({
  providedIn: 'root',
})
export class QueryParamsService {
  private filtersMemory: Partial<Record<ContextEnum, string | null>> = {};

  private static readonly FILTERS_STORAGE_PREFIX = 'lastFilters';
  private static readonly STATE_STORAGE_PREFIX = 'lastState';

  constructor(
    private sites: SitesService,
    private filterBridgeService: FilterBridgeService,
  ) {}

  applyQueryParams(queryParams: ParamMap, ctx: ContextEnum, forceApply = false, allowStoredState = true): boolean {
    const cluster = queryParams.get('cluster');
    let rangeCode = queryParams.get('range') as RangePreset | null;
    const fromDate = queryParams.get('fromDate') ?? queryParams.get('beginDate');
    const toDate = queryParams.get('toDate') ?? queryParams.get('endDate');
    const sitesParam = queryParams.get('sites');
    const tagsParam = queryParams.get('tags');

    const hasParams = !!(cluster || rangeCode || fromDate || sitesParam || tagsParam);

    const raw = !hasParams && allowStoredState ? this.getStoredState(ctx) : null;
    const stored = raw ? this.resolveStoredSelection(ctx, raw) : null;

    if (!hasParams && !stored && !forceApply) {
      return false;
    }

    if (!hasParams && stored) {
      rangeCode = stored.range as RangePreset | null;
    }

    const effectiveBegin = hasParams ? fromDate : (stored?.fromDate ?? null);
    const effectiveEnd = hasParams ? toDate : (stored?.toDate ?? null);

    if (!rangeCode && !(effectiveBegin && effectiveEnd)) {
      rangeCode = 'T' as RangePreset;
    }

    const range =
      rangeCode && RANGE_PRESETS[rangeCode]
        ? RANGE_PRESETS[rangeCode].refresh()
        : effectiveBegin && effectiveEnd
          ? new DateRange(moment(effectiveBegin), moment(effectiveEnd))
          : undefined;

    const current = hasParams ? null : this.sites[ctx].buildRequestData();

    const storedSites = stored?.sites ? stored.sites.split(',') : null;
    const storedTags = stored?.tags ? stored.tags.split(',') : null;

    const reqData: BaseLogRequest = {
      cluster: cluster ?? stored?.cluster ?? current?.cluster ?? null,
      owner: queryParams.get('owner') ?? stored?.owner ?? current?.owner ?? null,
      tags: tagsParam ? tagsParam.split(',') : (storedTags ?? current?.tags ?? null),
      sites: sitesParam ? sitesParam.split(',') : (storedSites ?? current?.sites ?? []),
      fromDate: range?.start ?? null,
      toDate: range?.end ?? null,
    };

    this.sites[ctx].buildCurrentFromRequestData(reqData, range);
    return true;
  }

  storeState(ctx: ContextEnum, params: BaseQueryParamsShape): void {
    if (!params?.cluster) return;
    try {
      sessionStorage.setItem(this.stateStorageKey(ctx), JSON.stringify(params));
    } catch {}
  }

  paramsMatchCurrentState(params: ParamMap, expected: Record<string, string | null>): boolean {
    for (const key of Object.keys(expected)) {
      const incoming = params.get(key);
      const a = incoming === '' || incoming === undefined ? null : incoming;
      const b = expected[key] === undefined ? null : expected[key];
      if (a !== b) return false;
    }
    return true;
  }

  getStoredState(ctx: ContextEnum): BaseQueryParamsShape | null {
    try {
      const raw = sessionStorage.getItem(this.stateStorageKey(ctx));
      return raw ? (JSON.parse(raw) as BaseQueryParamsShape) : null;
    } catch {
      return null;
    }
  }

  buildQueryParams(ctx: ContextEnum): BaseQueryParamsShape {
    const a = this.sites[ctx].buildRequestData();
    const range = this.sites[ctx].current.period.range;
    const presetCode = range instanceof DynamicRange ? range.code : null;
    return {
      cluster: a.cluster || null,
      range: presetCode || null,
      fromDate: !presetCode && a.fromDate?.isValid() ? a.fromDate.format('YYYY-MM-DDTHH:mm:ss.SSS') : null,
      toDate: !presetCode && a.toDate?.isValid() ? a.toDate.format('YYYY-MM-DDTHH:mm:ss.SSS') : null,
      owner: a.owner || null,
      sites: a.sites?.length ? a.sites.join(',') : null,
      tags: a.tags?.length ? a.tags.join(',') : null,
    };
  }

  storeFilters(ctx: ContextEnum, filters: string | null): void {
    this.filtersMemory[ctx] = filters;
    try {
      if (filters) {
        sessionStorage.setItem(this.filtersStorageKey(ctx), filters);
      } else {
        sessionStorage.removeItem(this.filtersStorageKey(ctx));
      }
    } catch {
      /* noop */
    }
  }

  getStoredFilters(ctx: ContextEnum): string | null {
    if (ctx in this.filtersMemory) {
      return this.filtersMemory[ctx] ?? null;
    }
    try {
      return sessionStorage.getItem(this.filtersStorageKey(ctx));
    } catch {
      return null;
    }
  }

  private filtersStorageKey(ctx: ContextEnum): string {
    return `${QueryParamsService.FILTERS_STORAGE_PREFIX}:${ctx}`;
  }

  private stateStorageKey(ctx: ContextEnum): string {
    return `${QueryParamsService.STATE_STORAGE_PREFIX}:${ctx}`;
  }

  private resolveStoredSelection(ctx: ContextEnum, stored: BaseQueryParamsShape): BaseQueryParamsShape {
    const store: any = this.sites[ctx].store;

    const clusters: any[] = store?.clusters ?? store?.clustersList ?? store?.clusterList ?? [];
    if (stored.cluster && clusters.length) {
      const known = clusters.some((c) => (c?.uid ?? c?.id ?? c) === stored.cluster);
      if (!known) return { ...stored, cluster: null, sites: null, tags: null };
    }

    const siteList: any[] = store?.sitesList ?? store?.sites ?? [];
    if (stored.sites && siteList.length) {
      const knownSites = new Set(siteList.map((s) => s?.domainName ?? s?.name ?? s));
      const kept = stored.sites.split(',').filter((s) => knownSites.has(s));
      return { ...stored, sites: kept.length ? kept.join(',') : null };
    }

    return stored;
  }

  serializeFiltersToUrl(filters: Filter[]): string | null {
    if (!filters?.length) return null;

    const parts = filters.map((f) => this.filterBridgeService.encodeFilter(f)).filter((s): s is string => s !== null);

    return parts.length ? parts.join('|') : null;
  }
  async deserializeFiltersFromUrl(raw: string, filtersComponent: FiltersComponent): Promise<Filter[]> {
    if (!raw) return [];

    const defs = filtersComponent?.allFilterDefinitions;
    if (!defs) return [];

    const chunks = raw.split('|');

    const keys = chunks
      .map((chunk) => this.filterBridgeService.decodeFilterKey(chunk))
      .filter((k): k is FiltersEnum => k !== null);

    await filtersComponent.ensureChoicesLoaded(keys);

    return chunks
      .map((chunk) => this.filterBridgeService.decodeFilter(chunk, defs))
      .filter((f): f is Filter => f !== null);
  }
}

export type BaseQueryParamsShape = {
  cluster: string | null;
  range: string | null;
  fromDate: string | null;
  toDate: string | null;
  owner: string | null;
  sites: string | null;
  tags: string | null;
};
